import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { take, takeUntil } from 'rxjs';

import { BaseComponent } from '../../../../core/components/base/base.component';
import {
  AcceptIncomingForm,
  FUTURE_MODE_LABELS,
  IncomingPracticeReferral,
  ReferralAcknowledgement,
  ReferralFutureMode,
  URGENCY_LABELS,
  acceptIncomingBody,
  acceptIncomingProblems,
  describeReferralFailure,
  diagnosisMeta,
  distinctMedications,
  incomingMatchSubject,
  workingDiagnosisLines,
} from '../../../../core/models/referral.model';
import { FeedbackService } from '../../../../core/services/feedback.service';
import { PatientService } from '../../../../core/services/patient.service';
import { ReferralService } from '../../../../core/services/referral.service';
import { ChatService } from '../../../../chat/chat.service';
import { UnlinkedCandidateMatchService, UnlinkedCandidateRow } from '../../../../core/services/unlinked-candidate-match.service';
import { PatientSelectionDialogComponent } from '../../../../shared/components/patient-selection-dialog/patient-selection-dialog.component';

export interface ReferralIncomingDialogData {
  receiptId: string;
  /** The practice whose inbox the row came from. */
  groupId: string;
}

export interface ReferralIncomingDialogResult {
  changed?: boolean;
  /** Set when the practice chose to share a summary back: the inbox opens the patient's referral dialog onto it. */
  summaryShareBack?: { patientId: string; patientName: string; linkId: string; receiptId: string; practiceName: string };
}

/** Decline reasons are shown to the referring practice; the server allows 1000 characters. */
export const INCOMING_DECLINE_MAX = 1000;

/**
 * A referral from another MedicAI practice (referrals architecture §19, P5, P8): the inbox opener for `referral-incoming`.
 *
 * Shows what was sent (who referred, why, how urgent, a summary if it is one, and the patient's details as sent), then two
 * decisions. ACCEPT links it to a patient of this practice and chooses how to share back, the way the referring practice
 * chose: the whole timeline (now and, if chosen, later), a summary (written next in the patient's referral dialog), or
 * nothing for now. A summary referral's text is filed on the patient as a read-only note when it is accepted.
 * DECLINE needs a reason; the referring practice sees it.
 *
 * WHICH PATIENT follows the unlinked pathology result pattern (unlinked-result-link-dialog): the practice's loaded patients
 * are ranked in the browser (UnlinkedCandidateMatchService: exact identity document, then name/date-of-birth similarity,
 * with the details and conflicts shown), "Search all patients" opens the patient picker, or the patient is created from
 * the details sent. Nothing is pre-selected: the user clicks the patient.
 */
@Component({
  selector: 'app-referral-incoming-dialog',
  templateUrl: './referral-incoming-dialog.component.html',
  styleUrls: ['../referral-note-review-dialog/referral-note-review-dialog.component.scss'],
})
export class ReferralIncomingDialogComponent extends BaseComponent implements OnInit {
  referral: IncomingPracticeReferral | null = null;
  acknowledgement: ReferralAcknowledgement | null = null;
  isLoading = true;
  loadError: string | null = null;
  isSaving = false;
  actionError: string | null = null;
  declining = false;
  declineReason = '';
  submitAttempted = false;
  ranking = true;
  exactCandidates: UnlinkedCandidateRow[] = [];
  fuzzyCandidates: UnlinkedCandidateRow[] = [];
  /** A patient chosen through "Search all patients" that the ranking did not list. */
  searchedPatient: { id: string; label: string } | null = null;
  form: AcceptIncomingForm = { patient: null, shareBack: null, futureMode: null, futureUntil: null, reason: '', acknowledged: false };

  readonly urgencyLabels = URGENCY_LABELS;
  readonly diagnosisMeta = diagnosisMeta;
  readonly workingDiagnosisLines = workingDiagnosisLines;
  readonly distinctMedications = distinctMedications;
  readonly futureModes: ReferralFutureMode[] = ['none', 'until', 'forever'];
  readonly futureModeLabels = FUTURE_MODE_LABELS;
  readonly declineMax = INCOMING_DECLINE_MAX;
  readonly minFutureDate = new Date(Date.now() + 24 * 60 * 60 * 1000);
  readonly shownFields: { key: string; label: string }[] = [
    { key: 'title', label: 'Title' }, { key: 'name', label: 'Name' }, { key: 'surname', label: 'Surname' }, { key: 'dob', label: 'Date of birth' },
    { key: 'gender', label: 'Gender' }, { key: 'idNumber', label: 'ID number' }, { key: 'passportNumber', label: 'Passport' },
    { key: 'mobileNumber', label: 'Mobile' }, { key: 'email', label: 'Email' }, { key: 'medicalAid', label: 'Medical aid' }, { key: 'medicalAidNumber', label: 'Member number' },
  ];

  /** The practice's loaded patient list (the inbox is the selected practice's), reused for "Search all patients". */
  private patients: any[] = [];
  /** M6: chat is on for this platform */
  chatEnabled = false;

  constructor(
    private referralService: ReferralService,
    private patientService: PatientService,
    private candidateMatchService: UnlinkedCandidateMatchService,
    private chat: ChatService,
    private dialogRef: MatDialogRef<ReferralIncomingDialogComponent, ReferralIncomingDialogResult>,
    @Inject(MAT_DIALOG_DATA) public data: ReferralIncomingDialogData,
    fs: FeedbackService,
    md: MatDialog,
  ) {
    super(fs, md);
  }

  ngOnInit(): void {
    this.chat.enabled$.pipe(takeUntil(this.destroy$)).subscribe((on) => (this.chatEnabled = on === true));
    void this.chat.ensureStarted();
    this.referralService.getIncomingReferral(this.data.receiptId, this.data.groupId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (referral) => {
        this.referral = referral;
        this.isLoading = false;
        if (referral.status === 'pending') this.rankCandidates(referral);
      },
      error: (error) => {
        this.isLoading = false;
        this.loadError = describeReferralFailure(error, 'This referral could not be loaded.');
      },
    });
    this.referralService.listRecipients().pipe(takeUntil(this.destroy$)).subscribe({
      next: (recipients) => { this.acknowledgement = recipients.acknowledgement; },
      error: () => { this.acknowledgement = null; },
    });
  }

  private rankCandidates(referral: IncomingPracticeReferral): void {
    this.patientService.patients$.pipe(take(1), takeUntil(this.destroy$)).subscribe((patients) => {
      this.patients = patients ?? [];
      this.candidateMatchService.rankCandidates(incomingMatchSubject(referral.patient), this.patients)
        .then((rows) => {
          this.exactCandidates = rows.filter((r) => r.kind === 'exact-identity');
          this.fuzzyCandidates = rows.filter((r) => r.kind === 'fuzzy');
          this.ranking = false;
        })
        .catch(() => {
          this.exactCandidates = [];
          this.fuzzyCandidates = [];
          this.ranking = false;
        });
    });
  }

  get patientName(): string {
    const p = this.referral?.patient;
    return p ? [p['name'], p['surname']].filter(Boolean).join(' ') || 'the patient' : 'the patient';
  }

  fieldValue(key: string): string | null {
    const value = this.referral?.patient?.[key];
    return value === null || value === undefined || value === '' ? null : String(value);
  }

  patientLabel(patient: any): string {
    const surname = String(patient?.surname ?? '').trim();
    const name = String(patient?.name ?? '').trim();
    const number = String(patient?.patientNumber ?? '').trim();
    const base = [surname, name].filter(Boolean).join(', ') || 'Unnamed patient';
    return number ? `${base} (${number})` : base;
  }

  /** Fallback when the ranking has nothing useful: the whole loaded patient list, as for an unlinked result. */
  searchAllPatients(): void {
    this.dialog.open(PatientSelectionDialogComponent, { width: '400px', data: { patients: this.patients } })
      .afterClosed().pipe(takeUntil(this.destroy$)).subscribe((selected: any) => {
        // The picker's close value is a patient, or 'invite' / 'event' / 'opd' (create something new): not a way to choose here.
        if (!selected) return;
        if (typeof selected === 'string') {
          this.showToastMessage('To create the patient, choose "A new patient" below.');
          return;
        }
        if (!selected.id) return;
        // A patient the ranking already lists is selected there; any other is added as its own choice.
        const same = (id: unknown) => String(id).toLowerCase() === String(selected.id).toLowerCase();
        const listed = [...this.exactCandidates, ...this.fuzzyCandidates].find((c) => same(c.patient.id));
        this.searchedPatient = listed ? null : { id: selected.id, label: this.patientLabel(selected) };
        this.form.patient = listed ? listed.patient.id! : selected.id;
      });
  }

  get problems(): string[] {
    const problems = acceptIncomingProblems(this.form, !!this.referral?.linkActive);
    if (this.form.shareBack === 'full' && !this.acknowledgement) problems.push('The acknowledgement could not be loaded. Close and try again.');
    return problems;
  }

  accept(): void {
    this.submitAttempted = true;
    if (!this.referral || this.problems.length || this.isSaving) return;
    this.isSaving = true;
    this.actionError = null;
    const body = acceptIncomingBody(this.form, this.acknowledgement?.version || '');
    this.referralService.acceptIncomingReferral(this.data.receiptId, body, this.data.groupId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (result) => {
        this.isSaving = false;
        const filed = result.summaryNoteId ? ' The summary is filed on the patient.' : '';
        this.showToastMessage((result.createdPatient ? 'Referral accepted and the patient created.' : 'Referral accepted.') + filed);
        const referral = this.referral!;
        this.dialogRef.close({
          changed: true,
          summaryShareBack: this.form.shareBack === 'summary' && referral.linkId
            ? { patientId: result.patientId, patientName: this.patientName, linkId: referral.linkId, receiptId: referral.id, practiceName: referral.senderPracticeName }
            : undefined,
        });
      },
      error: (error) => {
        this.isSaving = false;
        this.actionError = describeReferralFailure(error, 'The referral could not be accepted. Please try again.');
      },
    });
  }

  decline(): void {
    const reason = this.declineReason.trim();
    if (!reason || this.isSaving) return;
    this.isSaving = true;
    this.actionError = null;
    this.referralService.declineIncomingReferral(this.data.receiptId, reason, this.data.groupId).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.isSaving = false;
        this.showToastMessage('Referral declined. The referring practice sees your reason.');
        this.dialogRef.close({ changed: true });
      },
      error: (error) => {
        this.isSaving = false;
        this.actionError = describeReferralFailure(error, 'The referral could not be declined. Please try again.');
      },
    });
  }

  /** The referral chat with the referring practice (M6), in the floating chat window; this dialog closes. */
  async openChat(): Promise<void> {
    try {
      await this.chat.openReferralChat(this.data.receiptId, this.data.groupId);
      this.dialogRef.close({});
    } catch {
      this.showToastMessage('The referral chat could not be opened. Please try again.');
    }
  }

  close(): void {
    this.dialogRef.close({});
  }
}
